<script>
import BaseModalConfirm from '@/components/BaseModalConfirm.vue';
import DateTimeCacheDate from '@/components/dateTimeCache/DateTimeCacheDate.vue';
import { mapActions, mapState } from 'vuex';

import { convertDateTimeToUTC, datetimeFormat, scrollToTop } from '../../assets/js/helpers';

export default {
	name: 'DateTimeCache',
	components: {
		BaseModalConfirm,
		DateTimeCacheDate,
	},
	data() {
		return {
			selectedTag: null, // tag ที่ถูกเลือกสำหรับล้างแคช

			// ข้อมูลฟอร์มตั้งเวลา
			startDate: null,
			startTime: '00:00',
		};
	},
	computed: {
		...mapState('dateTimeCaches', {
			list: 'tags',
			config: 'config',
		}),

		// สถานะการโหลดข้อมูล
		isLoading() {
			return this.list.isLoading || this.config.isLoading;
		},

		// ดึงเฉพาะชื่อ tag จาก list
		tags() {
			return this.list.data.map((item) => item.tag);
		},
	},
	async created() {
		// โหลดข้อมูลเริ่มต้นเมื่อสร้าง component
		await this.getTags();
		await this.getConfig();

		// นำค่าเวลาที่ตั้งไว้มาใช้ในฟอร์ม
		const { autoClearCacheAt } = this.config.data;
		if (autoClearCacheAt) {
			this.startDate = new Date(datetimeFormat(autoClearCacheAt));
			this.startTime = datetimeFormat(autoClearCacheAt, 'HH:mm');
		}
	},
	methods: {
		...mapActions({
			purgeCacheDateTime: 'dateTimeCaches/purgeCacheDateTime',
			getTags: 'dateTimeCaches/getTags',
			getConfig: 'dateTimeCaches/getConfig',
			updateConfig: 'dateTimeCaches/updateConfig',
		}),

		/**
		 * แปลงวินาทีให้อยู่ในรูปแบบเวลาที่อ่านง่าย
		 * @param {number} secs จำนวนวินาที
		 * @returns {string} ข้อความแสดงระยะเวลา (เช่น 01:30 ชั่วโมง หรือ 05:00 นาที)
		 */
		secondsToTime(secs) {
			const hours = Math.floor(secs / 3600);
			const minutes = Math.floor((secs % 3600) / 60);
			const seconds = Math.ceil(secs % 60);

			if (hours) {
				return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ชั่วโมง`;
			}

			if (minutes) {
				return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} นาที`;
			}

			return `${seconds.toString().padStart(2, '0')} วินาที`;
		},

		/**
		 * จัดการเมื่อกดปุ่มล้างแคชเพื่อเปิด Modal ยืนยัน
		 * @param {string} tag ชื่อ tag ที่ต้องการล้างแคช
		 */
		onClickClear(tag) {
			this.selectedTag = tag;
			this.$refs['modal-clear-cache'].open();
		},

		/**
		 * รีเซ็ตค่า tag ที่เลือกเมื่อปิด Modal
		 */
		resetCache() {
			this.selectedTag = null;
		},

		/**
		 * เรียก API เพื่อล้างแคชตาม tag ที่เลือก
		 */
		async clearCache() {
			const tagsToPurge = this.selectedTag === 'all' ? this.tags : [this.selectedTag];
			await this.purgeCacheDateTime(tagsToPurge);
		},

		/**
		 * ยืนยันการเปลี่ยนแปลงการตั้งค่าเวลาล้างแคชอัตโนมัติ
		 */
		async onConfirm() {
			const dateRef = this.$refs.date.$v;
			dateRef.$touch();

			// ตรวจสอบความถูกต้องของข้อมูลก่อนบันทึก
			if (dateRef.$invalid) return;

			const payload = {
				auto_clear_cache_at: convertDateTimeToUTC(this.startDate, this.startTime),
			};

			try {
				await this.updateConfig(payload);
			} finally {
				scrollToTop();
			}
		},
	},
};
</script>

<template>
	<BaseLoading v-if="isLoading" is-full-page />
	<div v-else class="main-wrapper mt-4">
		<!-- ส่วนจัดการ: ล้างแคชทั้งหมด -->
		<CRow class="mb-4">
			<CCol md="9">
				<h4 class="search-weight-title">
					Clear date time cache
				</h4>
				<div class="description typo-body-2 color-black-45">
					ตั้งค่าควบคุมการล้างข้อมูลวันที่และเวลาสำหรับผู้ใช้งานแบบแมนนวล
				</div>
			</CCol>

			<CCol md="3" class="text-right">
				<button
					class="btn btn-secondary"
					@click="onClickClear('all')"
				>
					Clear all
				</button>
			</CCol>
		</CRow>

		<!-- รายการล้างแคชตามหมวดหมู่ -->
		<CRow v-for="item in list.data" :key="item.tag" class="mb-4">
			<CCol md="9">
				<p class="typo-body-1 search-weight-title mb-1">
					<span>{{ item.name || item.tag }}</span>
					<span class="custom-tooltip-wrapper ml-1">
						<FontAwesomeIcon :icon="['far', 'info-circle']" class="icon mb-1" />
						<span class="custom-tooltip-text">Tag: {{ item.tag }}</span>
					</span>
				</p>
				<div class="description typo-body-2 color-black-45">
					รีเซ็ตทุกๆ {{ secondsToTime(item.ttl) }}
				</div>
			</CCol>

			<CCol md="3" class="text-right">
				<button
					class="btn btn-secondary"
					@click="onClickClear(item.tag)"
				>
					Clear
				</button>
			</CCol>
		</CRow>

		<div class="divider"></div>

		<!-- ส่วนจัดการ: ฟอร์มตั้งเวลาล้างอัตโนมัติ -->
		<DateTimeCacheDate
			ref="date"
			:start-date.sync="startDate"
			:start-time.sync="startTime"
		/>

		<BaseActionPanelStickyFooter
			content-class="main-wrapper"
			button-confirm-text="Save"
			@onConfirm="onConfirm"
		/>

		<!-- Modal ยืนยันการล้างแคช -->
		<BaseModalConfirm
			ref="modal-clear-cache"
			title="ยืนยันการล้างแคชข้อมูลการค้นหา?"
			description="การกระทำนี้จะส่งผลต่อการค้นหา การค้นหาครั้งต่อไปจะทำการดึงข้อมูลใหม่จากระบบค้นหา"
			primary-button-text="ยืนยัน"
			@onClose="resetCache"
			@onConfirm="clearCache"
		/>
	</div>
</template>

<style scoped lang="scss">
.divider {
	border-top: 1px solid #ddd;
	margin: 32px 0;
}

/* Custom Tooltip Styles */
.custom-tooltip-wrapper {
	position: relative;
	display: inline-flex;

	.icon {
		cursor: pointer;
		font-size: 12px;
	}

	&:hover .custom-tooltip-text {
		visibility: visible;
		opacity: 1;
		transform: translateX(-50%) translateY(-8px);
	}
}

.custom-tooltip-text {
	visibility: hidden;
	width: auto;
	min-width: 80px;
	background-color: #333;
	color: #fff;
	text-align: center;
	border-radius: 4px;
	padding: 4px 8px;
	position: absolute;
	z-index: 10;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(0);
	opacity: 0;
	transition: opacity 0.2s, transform 0.2s;
	font-size: 12px;
	white-space: nowrap;
	pointer-events: none;

	/* Tooltip arrow */
	&::after {
		content: "";
		position: absolute;
		top: 100%;
		left: 50%;
		margin-left: -5px;
		border-width: 5px;
		border-style: solid;
		border-color: #333 transparent transparent transparent;
	}
}
</style>